import { ListTable, StatTile, StatTiles, toDateValue, useFormat, type CsvCell } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ReportFrame } from './reportFrame';
import { rangeQuery, unwrap, type ReportRange } from './reportRange';

type Day = HoComponents['schemas']['JournalDayDto'];

const n = (value: number | string | null | undefined) => Number(value ?? 0);

/** This month so far: the journal is kept by month. */
function thisMonth(): ReportRange {
  const today = new Date();
  return { from: toDateValue(new Date(today.getFullYear(), today.getMonth(), 1)), to: toDateValue(today), storeId: '' };
}

/**
 * The sales journal (REPORTS.md D5): one row per store and business day with what the books need: sales, discounts, charges,
 * tax by rate, rounding, total, refunds and takings per payment method. Shown as a table; the CSV has one column per tax rate
 * and per payment method, to import into the accounting software. At most 31 days at a time.
 */
export function JournalReportPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [range, setRange] = useState<ReportRange>(thisMonth);
  const r = (key: string) => t(`reports.journal.${key}` as I18nKey);
  const journal = useQuery({
    queryKey: ['reports', 'journal', range],
    queryFn: () => unwrap<Day[]>(hoApi.GET('/api/v1/reporting/journal', { params: { query: rangeQuery(range) } })),
  });
  const days = journal.data ?? [];
  const sum = (pick: (d: Day) => number | string) => days.reduce((total, d) => total + n(pick(d)), 0);

  const csv = (): CsvCell[][] => {
    const taxes = [...new Map(days.flatMap((d) => d.taxes).map((x) => [`${x.name}|${n(x.rate)}`, x])).values()].sort(
      (a, b) => n(b.rate) - n(a.rate),
    );
    const methods = [...new Map(days.flatMap((d) => d.tenders).map((m) => [m.paymentMethodId, m.name])).entries()];
    return [
      [
        r('date'),
        r('storeCode'),
        r('store'),
        r('orders'),
        r('subtotal'),
        r('discounts'),
        r('charges'),
        ...taxes.flatMap((x) => [`${x.name} ${r('taxable')}`, `${x.name} ${r('tax')}`]),
        r('tax'),
        r('rounding'),
        r('total'),
        r('refunds'),
        ...methods.map(([, name]) => name),
      ],
      ...days.map((d) => [
        d.businessDate,
        d.storeCode,
        d.storeName,
        n(d.orders),
        n(d.subtotal),
        n(d.discounts),
        n(d.charges),
        ...taxes.flatMap((x) => {
          const line = d.taxes.find((y) => y.name === x.name && n(y.rate) === n(x.rate));
          return [n(line?.taxableAmount), n(line?.taxAmount)];
        }),
        n(d.tax),
        n(d.rounding),
        n(d.total),
        n(d.refunds),
        ...methods.map(([id]) => n(d.tenders.find((m) => m.paymentMethodId === id)?.net)),
      ]),
    ];
  };

  return (
    <ReportFrame
      title={r('title')}
      description={r('description')}
      range={range}
      onRange={setRange}
      error={journal.error}
      csvName="sales-journal"
      csv={days.length > 0 ? csv : undefined}
    >
      <StatTiles>
        <StatTile label={r('total')} value={format.number(sum((d) => d.total))} isLoading={journal.isLoading} />
        <StatTile label={r('tax')} value={format.number(sum((d) => d.tax))} isLoading={journal.isLoading} />
        <StatTile label={r('refunds')} value={format.number(sum((d) => d.refunds))} isLoading={journal.isLoading} />
        <StatTile label={r('orders')} value={format.number(sum((d) => d.orders))} isLoading={journal.isLoading} />
      </StatTiles>
      <ListTable<Day & { id: string }>
        title={r('byDay')}
        description={r('note')}
        columns={[
          { key: 'businessDate', header: r('date'), render: (d) => format.date(d.businessDate) },
          { key: 'storeName', header: r('store'), render: (d) => d.storeName || d.storeCode },
          { key: 'orders', header: r('orders'), numeric: true, render: (d) => format.number(n(d.orders)) },
          { key: 'subtotal', header: r('subtotal'), numeric: true, render: (d) => format.number(n(d.subtotal)) },
          { key: 'discounts', header: r('discounts'), numeric: true, render: (d) => format.number(n(d.discounts)) },
          { key: 'charges', header: r('charges'), numeric: true, render: (d) => format.number(n(d.charges)) },
          {
            key: 'tax',
            header: r('tax'),
            render: (d) =>
              d.taxes.map((x) => `${x.name} ${format.number(n(x.taxAmount))}`).join(' · ') || format.number(n(d.tax)),
          },
          {
            key: 'rounding',
            header: r('rounding'),
            numeric: true,
            render: (d) => (n(d.rounding) === 0 ? '' : format.number(n(d.rounding))),
          },
          { key: 'total', header: r('total'), numeric: true, render: (d) => format.number(n(d.total)) },
          {
            key: 'refunds',
            header: r('refunds'),
            numeric: true,
            render: (d) => (n(d.refunds) === 0 ? '' : format.number(n(d.refunds))),
          },
          {
            key: 'tenders',
            header: r('tenders'),
            render: (d) => d.tenders.map((m) => `${m.name} ${format.number(n(m.net))}`).join(' · '),
          },
        ]}
        rows={days.map((d) => ({ ...d, id: `${d.storeId}-${d.businessDate}` }))}
        ariaLabel={r('title')}
        loading={journal.isLoading}
        empty={{ heading: t('reports.noSales') }}
      />
    </ReportFrame>
  );
}
